import React, { useState } from 'react';
import './UserEditPanel.css';
import { defaultRoles } from './RoleManagement';
import { depts } from './UserManagement';

interface UserEditPanelProps {
    user: any | null;
    onClose: () => void;
    onSave: (updatedUser: any) => void;
}

const UserEditPanel: React.FC<UserEditPanelProps> = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState(user || {
        name: '',
        email: '',
        departments: ['본사 (전체)'],
        roleId: 'r3',
        status: 'pending' // Default new user to pending
    });

    if (!user && formData.status !== 'pending' && !formData.name) {
        // Just empty user check
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleDeptToggle = (deptStr: string, isChecked: boolean) => {
        if (deptStr === '본사 (전체)') {
            setFormData({ ...formData, departments: isChecked ? [...depts] : [] });
        } else {
            let newDepts = isChecked 
                ? [...formData.departments, deptStr] 
                : formData.departments.filter((d: string) => d !== deptStr);
            
            if (!isChecked) {
                newDepts = newDepts.filter((d: string) => d !== '본사 (전체)');
            } else if (depts.filter(d => d !== '본사 (전체)').every(d => newDepts.includes(d))) {
                newDepts.push('본사 (전체)');
            }
            setFormData({ ...formData, departments: newDepts });
        }
    };

    const handleSave = () => {
        if (formData.status === 'active' && formData.departments.length === 0) {
            alert("활성 상태의 계정은 최소 한 개의 접근 허용 부서를 지정해야 합니다.");
            return;
        }
        onSave(formData);
    };

    const selectedRole = defaultRoles.find(r => r.id === formData.roleId);
    const isNew = !user;

    return (
        <div className="user-edit-panel">
            <div className="panel-header" style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
                    {isNew ? '신규 관리자 수동 추가' : (formData.status === 'pending' ? '가입 승인 심사' : '사용자 권한 설정')}
                </h3>
                <button className="panel-close-btn" onClick={onClose}>&times;</button>
            </div>
            
            <div className="panel-body" style={{ padding: '24px' }}>
                <div className="panel-section" style={{ marginBottom: '32px' }}>
                    <h4 style={{ fontSize: '15px', color: '#0f172a', marginBottom: '16px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>1. 계정 기본 정보</h4>
                    <div className="form-group-panel">
                        <label>이름</label>
                        <input type="text" name="name" value={formData.name} readOnly disabled style={{ background: '#f1f5f9', color: '#64748b' }} />
                    </div>
                    <div className="form-group-panel">
                        <label>이메일 계정 (ID)</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={!isNew} style={{ background: !isNew ? '#f1f5f9' : 'white' }} />
                    </div>
                </div>

                {formData.status === 'active' || isNew ? (
                    <>
                        <div className="panel-section" style={{ marginBottom: '32px' }}>
                            <h4 style={{ fontSize: '15px', color: '#0f172a', marginBottom: '16px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>2. 데이터 접근 범위 다중 할당</h4>
                            <p className="field-hint-panel" style={{ marginBottom: '12px' }}>이 사용자가 모니터링하고 제어할 수 있는 현장(지점)의 데이터 범위를 복수로 지정합니다.</p>
                            <div className="form-group-panel">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    {depts.map(d => (
                                        <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={formData.departments.includes(d)} 
                                                onChange={(e) => handleDeptToggle(d, e.target.checked)} 
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            {d}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="panel-section" style={{ marginBottom: '32px' }}>
                            <h4 style={{ fontSize: '15px', color: '#0f172a', marginBottom: '16px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>3. 시스템 권한 그룹 부여</h4>
                            <div className="form-group-panel">
                                <select name="roleId" value={formData.roleId} onChange={handleChange} style={{ fontWeight: 600, padding: '12px', height: 'auto' }}>
                                    {defaultRoles.map(role => (
                                        <option key={role.id} value={role.id}>{role.name}</option>
                                    ))}
                                </select>
                            </div>
                            {selectedRole && (
                                <div className="role-preview-box" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '16px', borderRadius: '8px', marginTop: '12px' }}>
                                    <strong style={{ color: '#166534', display: 'block', marginBottom: '8px' }}>✓ {selectedRole.name} 권한 요약</strong>
                                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#15803d', lineHeight: '1.6' }}>
                                        <li>관리 메뉴: {selectedRole.permissions.management_fleet.preset === 'full' ? '최고 권한' : selectedRole.permissions.management_fleet.preset === 'operate' ? '일반 권한' : selectedRole.permissions.management_fleet.preset === 'view' ? '조회 전용' : selectedRole.permissions.management_fleet.preset === 'none' ? '접근 차단' : '직접 설정'}</li>
                                        <li>관제 메뉴: {selectedRole.permissions.control_battery.preset === 'full' ? '최고 권한' : selectedRole.permissions.control_battery.preset === 'operate' ? '일반 권한' : selectedRole.permissions.control_battery.preset === 'view' ? '조회 전용' : selectedRole.permissions.control_battery.preset === 'none' ? '접근 차단' : '직접 설정'}</li>
                                        <li>운영 메뉴: {selectedRole.permissions.linkage_driving.preset === 'full' ? '최고 권한' : selectedRole.permissions.linkage_driving.preset === 'operate' ? '일반 권한' : selectedRole.permissions.linkage_driving.preset === 'view' ? '조회 전용' : selectedRole.permissions.linkage_driving.preset === 'none' ? '접근 차단' : '직접 설정'}</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="panel-section" style={{ marginBottom: '32px' }}>
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '16px', borderRadius: '8px', color: '#b91c1c', fontSize: '14px', textAlign: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '8px', display: 'inline-block' }}><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                            <br/>
                            <strong style={{ fontSize: '15px' }}>{formData.status === 'pending' ? '가입 심사 대기 중인 사용자입니다.' : '이용 정지 조치된 계정입니다.'}</strong><br/>
                            {formData.status === 'pending' ? '가입을 승인(활성 상태로 변경)하면 데이터 접근 범위와 권한을 할당하는 영역이 활성화됩니다.' : "정지된 동안에는 기존 데이터 열람 권한이 원천 차단됩니다. 정상 상태로 복구 시 권한 설정을 변경할 수 있습니다."}
                        </div>
                    </div>
                )}

                <div className="panel-section">
                    <h4 style={{ fontSize: '15px', color: '#0f172a', marginBottom: '16px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>4. 계정 상태 제어</h4>
                    <div className="form-group-panel radio-group" style={{ display: 'flex', gap: '20px' }}>
                        {(formData.status === 'pending') && (
                            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input type="radio" name="status" value="pending" checked={formData.status === 'pending'} onChange={handleChange} />
                                승인 대기
                            </label>
                        )}
                        <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="radio" name="status" value="active" checked={formData.status === 'active'} onChange={handleChange} />
                            정상 (활성)
                        </label>
                        <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444' }}>
                            <input type="radio" name="status" value="suspended" checked={formData.status === 'suspended'} onChange={handleChange} />
                            이용 정지
                        </label>
                    </div>
                </div>
            </div>

            <div className="panel-footer" style={{ padding: '20px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button className="btn-save-global outline" onClick={onClose} style={{ border: '1px solid #cbd5e1', color: '#475569' }}>취소</button>
                <button className="btn-save-global" onClick={handleSave}>{formData.status === 'pending' ? '승인 보류' : (isNew ? '사용자 추가' : '설정 저장')}</button>
            </div>
        </div>
    );
};

export default UserEditPanel;
